import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { CAvatar, CButton, CDropdown, CDropdownDivider, CDropdownItem, CDropdownMenu, CDropdownToggle, CSpinner } from '@coreui/react'
import { cilAccountLogout } from '@coreui/icons'
import CIcon from '@coreui/icons-react'
import iconImg from './../../assets/images/avatars/brain.gif'
import { useHistory } from 'react-router-dom'
import { removeCookie, getRolefromCookie } from './../../utils/handleCookie'
import SaveNotification from '../../services/general/notificationApi'
import SaveTasks from '../../services/general/TaskCrud.api'
import providerCredentialingCRUD from '../../services/general/ProviderCredentialingCRUD.api'
import { Bell, Moon, Sun } from 'lucide-react'
import toast from 'react-hot-toast'
import { toastConfig } from '../../constants/other.config'
import RegisterUser from '../../services/general/register.api'
import { formatValueToMMDDYYYY, parseUSDateValue } from '../shared/customDateFormatUtils'
import Pageloader from '../shared/Pageloader'

const NOTIFICATION_LAST_SEEN_KEY = 'provio_notifications_last_seen_at'
const NOTIFICATION_REFERENCE_CACHE_KEY = 'provio_notification_references_v1'
const BIRTHDAY_USERS_CACHE_KEY = 'provio_birthday_notification_users_v1'
const BIRTHDAY_SEEN_KEY = 'provio_birthday_notifications_seen'
const APP_THEME_STORAGE_KEY = 'provio_theme'

const readAppTheme = () => localStorage.getItem(APP_THEME_STORAGE_KEY) || 'dark'

const applyAppTheme = (nextTheme) => {
  document.documentElement.setAttribute('data-theme', nextTheme)
  document.body.setAttribute('data-theme', nextTheme)
  document.body.classList.toggle('dark-mode', nextTheme !== 'light')
  document.body.classList.toggle('light-mode', nextTheme === 'light')
}

const normalizeIdentity = (value) => String(value || '').trim().replace(/\s+/g, '').toLowerCase()

const buildSubtaskReminders = (task, userId, username, scope, now = new Date()) => {
  const asArray = (value) => {
    try {
      const parsed = typeof value === 'string' ? JSON.parse(value) : value
      return Array.isArray(parsed) ? parsed : parsed ? [parsed] : []
    } catch { return value ? String(value).split(',').map((item) => item.trim()) : [] }
  }
  const identities = [userId, username].filter(Boolean).map(normalizeIdentity)
  const taskId = task.TaskId ?? task.Id ?? task.id
  if (taskId == null) return []
  return asArray(task.SubtasksJson ?? task.subtasksJson ?? task.subtasks_json ?? task.subtasks ?? task.subtask ?? task.subTasks ?? task.SubTasks).filter(Boolean).flatMap((subtask, index) => {
    const reminderDate = subtask.reminderDate ?? subtask.ReminderDate
    const date = parseUSDateValue(reminderDate)
    const subtaskAssignees = asArray(subtask.assignee ?? subtask.Assignee ?? subtask.assignees ?? subtask.AssignedTo)
    const parentAssignees = asArray(task.AssignedUsersJson ?? task.assignedUsersJson ?? task.assigned_users_json ?? task.assignedUsers ?? task.Assignee ?? task.assignee)
    const assignees = subtaskAssignees.length ? subtaskAssignees : parentAssignees.length ? parentAssignees : asArray(task.assigned_to)
    const isAssigned = assignees.some((user) =>
      (typeof user === 'object' && user ? [user.UserId, user.userId, user.Id, user.id, user.Username, user.username, user.value, user.label] : [user])
        .some((value) => value != null && identities.includes(normalizeIdentity(value))),
    )
    if (!date || date > now || !isAssigned) return []
    const name = subtask.text || subtask.Text || subtask.Title || subtask.title || 'Subtask'
    return [{ id: `subtask-reminder:${scope}:${taskId}:${subtask.SubtaskId ?? subtask.id ?? subtask.Id ?? index}:${reminderDate}`, title: 'Subtask Reminder', message: `Reminder for subtask "${name}" in "${task.Title || task.title || 'Task'}" on ${formatValueToMMDDYYYY(reminderDate)}`, createdDate: reminderDate, ...(scope ? { RelatedEntityType: 'Credentialing', RelatedEntityId: taskId } : { TaskId: taskId }) }]
  })
}

const readBirthdayUsersCache = () => {
  if (typeof window === 'undefined') return []

  try {
    const users = JSON.parse(localStorage.getItem(BIRTHDAY_USERS_CACHE_KEY) || '[]')
    return Array.isArray(users) ? users : []
  } catch {
    return []
  }
}

const writeBirthdayUsersCache = (users = []) => {
  if (typeof window === 'undefined') return

  try {
    localStorage.setItem(BIRTHDAY_USERS_CACHE_KEY, JSON.stringify(users))
  } catch {
    // Keep notifications functional when browser storage is unavailable.
  }
}

const readNotificationReadIds = (u) => {
  try { return new Set(JSON.parse(localStorage.getItem(`provio_read_notification_ids_${u || 'anonymous'}`) || '[]')) } catch { return new Set() }
}
const markNotificationReadLocally = (u, id) => {
  try {
    if (!u || !id) return
    const s = readNotificationReadIds(u)
    s.add(String(id))
    localStorage.setItem(`provio_read_notification_ids_${u || 'anonymous'}`, JSON.stringify([...s]))
  } catch {}
}

const getBirthdaySeenStorageKey = (userId) => `${BIRTHDAY_SEEN_KEY}_${userId || 'anonymous'}`

const readBirthdaySeenIds = (userId) => {
  if (!userId || typeof window === 'undefined') return new Set()

  try {
    const ids = JSON.parse(localStorage.getItem(getBirthdaySeenStorageKey(userId)) || '[]')
    return new Set(Array.isArray(ids) ? ids : [])
  } catch {
    return new Set()
  }
}

const markBirthdayNotificationsSeen = (userId, notificationIds = []) => {
  if (!userId || !notificationIds.length || typeof window === 'undefined') return

  try {
    const seenIds = readBirthdaySeenIds(userId)
    notificationIds.forEach((id) => seenIds.add(id))
    localStorage.setItem(getBirthdaySeenStorageKey(userId), JSON.stringify([...seenIds]))
  } catch {
    // Keep the existing notification read flow working if storage fails.
  }
}

const parseBirthdayUserList = (response) => {
  const raw = response?.d ?? response
  if (Array.isArray(raw)) return raw

  if (typeof raw === 'string') {
    try {
      return parseBirthdayUserList(JSON.parse(raw))
    } catch {
      return []
    }
  }

  return Array.isArray(raw?.Users)
    ? raw.Users
    : Array.isArray(raw?.users)
      ? raw.users
      : Array.isArray(raw?.Data)
        ? raw.Data
        : Array.isArray(raw?.data)
          ? raw.data
          : []
}

const normalizeBirthdayUser = (user = {}) => {
  let extraData = {}
  try {
    extraData = typeof user?.ExtraData === 'string' ? JSON.parse(user.ExtraData || '{}') : user?.ExtraData || {}
  } catch {
    extraData = {}
  }

  return {
    ...user,
    ID: user?.Id ?? user?.ID ?? user?.UserId ?? user?.userId ?? '',
    UserId: user?.UserId ?? user?.userId ?? user?.Id ?? user?.ID ?? '',
    UserName: user?.Username ?? user?.UserName ?? user?.username ?? '',
    Name: user?.Name || user?.FullName || extraData?.fullName || user?.Username || user?.Email || '',
    DOB: user?.DOB || user?.DateOfBirth || user?.dob || extraData?.dob || '',
    Status: user?.Status || user?.status || extraData?.status || 'Active',
  }
}

const formatNotificationLabel = (value, fallback = 'Notification') => {
  const label = String(value || '').trim()
  if (!label) return fallback

  return label
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
}

const readNotificationReferenceCache = () => {
  if (typeof window === 'undefined') {
    return { users: [], providers: [], employees: [] }
  }

  try {
    const raw = sessionStorage.getItem(NOTIFICATION_REFERENCE_CACHE_KEY)
    if (!raw) return { users: [], providers: [], employees: [] }

    const parsed = JSON.parse(raw)
    return {
      users: Array.isArray(parsed?.users) ? parsed.users : [],
      providers: Array.isArray(parsed?.providers) ? parsed.providers : [],
      employees: Array.isArray(parsed?.employees) ? parsed.employees : [],
    }
  } catch {
    return { users: [], providers: [], employees: [] }
  }
}

const writeNotificationReferenceCache = ({ users = [], providers = [], employees = [] } = {}) => {
  if (typeof window === 'undefined') return

  try {
    sessionStorage.setItem(NOTIFICATION_REFERENCE_CACHE_KEY, JSON.stringify({ users, providers, employees, loaded: true }))
  } catch {
    // Ignore storage failures so notifications still render.
  }
}

const readLoggedInUserIdentity = () => {
  if (typeof window === 'undefined') return null

  const sources = ['user', 'jwt_token']

  for (const key of sources) {
    try {
      const raw = localStorage.getItem(key)
      if (!raw) continue

      const parsed = JSON.parse(raw)
      return {
        id: parsed?.Id || parsed?.ID || parsed?.id || parsed?.UserId || parsed?.userId || '',
        username: parsed?.Username || parsed?.UserName || parsed?.username || '',
        name:
          parsed?.Name ||
          parsed?.name ||
          parsed?.FullName ||
          parsed?.fullName ||
          (parsed?.firstName ? `${parsed.firstName} ${parsed?.lastName || ''}`.trim() : '') ||
          parsed?.Username ||
          parsed?.UserName ||
          parsed?.username ||
          '',
        role: String(parsed?.Role || parsed?.role || parsed?.RoleName || parsed?.roleName || '').toLowerCase(),
      }
    } catch {
      // Try the next auth storage key.
    }
  }

  return null
}

const formatRoleLabel = (role) => {
  const normalizedRole = String(role || '').trim().toLowerCase()
  const roleLabels = {
    callcenter: 'Call Center',
    backoffice: 'Back Office',
    'back office': 'Back Office',
  }

  if (roleLabels[normalizedRole]) return roleLabels[normalizedRole]

  return normalizedRole
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

const getInitials = (name, fallback = '') => {
  const parts = String(name || fallback || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)

  if (parts.length === 0) return 'U'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()

  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
}

const buildNotificationIdentityMap = ({ users = [], providers = [], employees = [] } = {}, loggedInUser = null) => {
  const map = new Map()

  const loggedInUserName = String(loggedInUser?.name || '').trim()
  if (loggedInUserName) {
    ;[
      loggedInUser?.id,
      loggedInUser?.username,
      loggedInUser?.name,
    ].forEach((value) => {
      const key = normalizeIdentity(value)
      if (key && !map.has(key)) {
        map.set(key, loggedInUserName)
      }
    })
  }

  providers.forEach((provider) => {
    const displayName = String(
      provider?.Name ||
      provider?.ProviderName ||
      provider?.FullName ||
      provider?.name ||
      provider?.Username ||
      provider?.UserName ||
      '',
    ).trim()

    if (!displayName) return

      ;[
        provider?.ID,
        provider?.Id,
        provider?.id,
        provider?.UserId,
        provider?.UserID,
        provider?.Username,
        provider?.UserName,
        provider?.Name,
        provider?.ProviderName,
      ].forEach((value) => {
        const key = normalizeIdentity(value)
        if (key && !map.has(key)) {
          map.set(key, displayName)
        }
      })
  })

  employees.forEach((employee) => {
    const displayName = String(
      employee?.EmployeeName ||
      employee?.employeeName ||
      employee?.Name ||
      employee?.name ||
      employee?.FullName ||
      '',
    ).trim()

    if (!displayName) return

      ;[
        employee?.EmployeeId,
        employee?.employeeId,
        employee?.ID,
        employee?.Id,
        employee?.id,
        employee?.UserId,
        employee?.UserID,
        employee?.Username,
        employee?.username,
        employee?.UserName,
        employee?.EmployeeName,
        employee?.employeeName,
        employee?.Name,
      ].forEach((value) => {
        const key = normalizeIdentity(value)
        if (key && !map.has(key)) {
          map.set(key, displayName)
        }
      })
  })

  users.forEach((user) => {
    const displayName = getBirthdayDisplayName(user)
    if (!displayName) return

    getBirthdayIdentityCandidates(user).forEach((value) => {
      const key = normalizeIdentity(value)
      if (key && !map.has(key)) {
        map.set(key, displayName)
      }
    })
  })

  return map
}

const getNotificationActorCandidates = (item = {}, message = '') => {
  const directNameCandidates = [
    item?.EmployeeName,
    item?.employeeName,
    item?.LeaveEmployeeName,
    item?.leaveEmployeeName,
    item?.RequestedForName,
    item?.requestedForName,
    item?.RequesterName,
    item?.requesterName,
    item?.RequestedByName,
    item?.requestedByName,
    item?.FullName,
    item?.fullName,
  ]

  const identityCandidates = [
    item?.EmployeeId,
    item?.employeeId,
    item?.UserId,
    item?.userId,
    item?.Name,
    item?.name,
    item?.ProviderName,
    item?.providerName,
    item?.FullName,
    item?.fullName,
    item?.UserName,
    item?.Username,
    item?.username,
    item?.UserId,
    item?.userId,
  ]

  const messageMatch = String(message || '').match(/^(.*?)\s+submitted\s+leave\s*request\b/i)
  if (messageMatch?.[1]) {
    directNameCandidates.unshift(messageMatch[1])
  }

  return [...directNameCandidates, ...identityCandidates]
    .map((value) => String(value || '').trim())
    .filter(Boolean)
}

const resolveNotificationActorName = (item = {}, message = '', identityMap = new Map(), loggedInUser = null) => {
  const candidates = getNotificationActorCandidates(item, message)
  const loggedInUserId = normalizeIdentity(loggedInUser?.id)
  const loggedInUsername = normalizeIdentity(loggedInUser?.username)
  const loggedInUserName = String(loggedInUser?.name || '').trim()

  for (const candidate of candidates) {
    const normalizedCandidate = normalizeIdentity(candidate)
    const resolved = identityMap.get(normalizedCandidate)
    if (resolved) return resolved

    if (loggedInUserName && normalizedCandidate && (
      normalizedCandidate === loggedInUserId ||
      normalizedCandidate === loggedInUsername
    )) {
      return loggedInUserName
    }
  }

  const firstCandidate = candidates[0] || ''
  if (/^\d+$/.test(firstCandidate) && loggedInUserName) {
    return loggedInUserName
  }

  return firstCandidate
}

const formatNotificationMessage = (item = {}, identityMap = new Map(), loggedInUser = null) => {
  const rawMessage = String(item?.message || '').trim()
  if (!rawMessage) return ''

  const normalizedMessage = rawMessage
    .replace(/leave\s*request/gi, 'leave request')
    .replace(/\b(\d{4}-\d{2}-\d{2}|\d{1,2}\/\d{1,2}\/\d{4})\b/g, (match) => formatValueToMMDDYYYY(match) || match)
  const actorName = resolveNotificationActorName(item, normalizedMessage, identityMap, loggedInUser)

  if (!actorName) return normalizedMessage

  return normalizedMessage.replace(/^(.*?)\s+submitted\s+leave\s*request\b/i, `${actorName} submitted leave request`)
}

const parseBirthdayDate = (value) => {
  return parseUSDateValue(value)
}

const isBirthdayToday = (value, today = new Date()) => {
  const parsedDate = parseBirthdayDate(value)
  if (!parsedDate) return false

  return parsedDate.getMonth() === today.getMonth() && parsedDate.getDate() === today.getDate()
}

const getBirthdayDisplayName = (item = {}) =>
  String(
    item?.Name ||
      item?.name ||
      item?.EmployeeName ||
      item?.employeeName ||
      item?.ProviderName ||
      item?.providerName ||
      item?.FullName ||
      item?.fullName ||
      item?.Username ||
      item?.UserName ||
      '',
  ).trim()

const getBirthdayIdentityCandidates = (item = {}) =>
  [
    item?.ID,
    item?.Id,
    item?.id,
    item?.UserId,
    item?.UserID,
    item?.EmployeeId,
    item?.employeeId,
    item?.Username,
    item?.username,
    item?.UserName,
    item?.Name,
    item?.name,
    item?.EmployeeName,
    item?.employeeName,
    item?.ProviderName,
    item?.providerName,
    item?.FullName,
    item?.fullName,
  ]
    .map((value) => String(value || '').trim())
    .filter(Boolean)

const isActiveBirthdayRecord = (item = {}) => {
  const status = String(item?.Status ?? item?.status ?? item?.ActiveStatus ?? item?.activeStatus ?? '').trim().toLowerCase()
  const isDeleted = [item?.IsDeleted, item?.isDeleted, item?.Deleted, item?.deleted].some((value) => value === true || value === 1 || String(value).trim().toLowerCase() === 'true')
  const isInactive = [item?.IsActive, item?.isActive, item?.Active, item?.active].some((value) => value === false || value === 0 || String(value).trim().toLowerCase() === 'false')

  return !isDeleted && !isInactive && !['inactive', 'deactive', 'deactivated', 'deleted', 'disabled'].includes(status)
}

const mergeBirthdayRecords = (records = []) => {
  const aliases = new Map()
  const mergedRecords = new Map()

  records.forEach((record) => {
    const identities = getBirthdayIdentityCandidates(record).map(normalizeIdentity).filter(Boolean)
    const existingKey = identities.map((identity) => aliases.get(identity)).find(Boolean)
    const key = existingKey || identities[0]
    if (!key) return

    const existing = mergedRecords.get(key)
    const merged = existing
      ? {
        ...existing,
        ...record,
        Name: getBirthdayDisplayName(record) || getBirthdayDisplayName(existing),
        DOB: record?.DOB || record?.dob || existing?.DOB || existing?.dob || '',
        IsActive: isActiveBirthdayRecord(existing) && isActiveBirthdayRecord(record),
      }
      : record

    mergedRecords.set(key, merged)
    identities.forEach((identity) => aliases.set(identity, key))
  })

  return Array.from(mergedRecords.values())
}

const isBirthdayForLoggedInUser = (item = {}, loggedInUser = null) => {
  const loggedUserKeys = [
    loggedInUser?.id,
    loggedInUser?.username,
    loggedInUser?.name,
  ]
    .map((value) => normalizeIdentity(value))
    .filter(Boolean)

  if (loggedUserKeys.length === 0) return false

  return getBirthdayIdentityCandidates(item).some((candidate) => loggedUserKeys.includes(normalizeIdentity(candidate)))
}

const resolveBirthdayReadState = (userId = '', notificationId = '') => readBirthdaySeenIds(userId).has(notificationId)

const buildBirthdayNotifications = (users = [], loggedInUser = null, userId = '') => {
  const today = new Date()
  const seenKeys = new Set()
  const combinedRecords = mergeBirthdayRecords(users)

  // A logged-in user is an eligible recipient even when their profile is not
  // present in an HRM reference response (common for Admin/Super Admin users).
  // Only suppress the notification if a matching record explicitly says that
  // recipient is inactive or deleted.
  const loggedInUserRecords = combinedRecords.filter((item) => isBirthdayForLoggedInUser(item, loggedInUser))
  if (loggedInUserRecords.some((item) => !isActiveBirthdayRecord(item))) return []

  return combinedRecords
    .filter((item) => isActiveBirthdayRecord(item) && isBirthdayToday(item?.DOB || item?.dob, today))
    .map((item) => {
      const name = getBirthdayDisplayName(item)
      if (!name) return null

      const birthdayUserId = getBirthdayIdentityCandidates(item).map(normalizeIdentity).find(Boolean) || normalizeIdentity(name)
      const dateKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
      const dedupeKey = `birthday:${birthdayUserId}:${normalizeIdentity(userId)}:${dateKey}`
      if (seenKeys.has(dedupeKey)) return null
      seenKeys.add(dedupeKey)

      const isOwnBirthday = isBirthdayForLoggedInUser(item, loggedInUser)
      const startOfDay = new Date()
      startOfDay.setHours(0, 0, 0, 0)
      const createdDate = startOfDay.toISOString()

      return {
        id: dedupeKey,
        title: isOwnBirthday ? 'It’s Your Birthday Today!' : `It’s ${name}’s Birthday Today!`,
        message: isOwnBirthday
          ? 'Happy Birthday! Wishing you a wonderful day filled with happiness and success.'
          : `Wish ${name} a very happy birthday and make their day special.`,
        createdDate,
        isRead: resolveBirthdayReadState(userId, dedupeKey),
        type: 'birthday',
        isSyntheticBirthday: true,
      }
    })
    .filter(Boolean)
}

const getCredentialingNotificationUrl = (notification = {}) => {
  const notificationText = [
    notification?.RelatedEntityType,
    notification?.relatedEntityType,
    notification?.EntityType,
    notification?.entityType,
    notification?.title,
    notification?.Title,
    notification?.message,
    notification?.Message,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()

  if (!notificationText.includes('credential')) return ''

  const rawProviderId = notification?.ProviderID ?? notification?.ProviderId ?? notification?.providerId
  const rawTaskId = notification?.TaskID ?? notification?.TaskId ?? notification?.taskId ?? notification?.CredentialingTaskID ?? notification?.credentialingTaskId ?? notification?.RelatedEntityId ?? notification?.relatedEntityId
  const params = new URLSearchParams()

  if (rawTaskId != null && String(rawTaskId).trim() !== '') {
    params.set('taskId', String(rawTaskId))
  }

  if (rawProviderId != null && String(rawProviderId).trim() !== '') {
    const providerId = String(rawProviderId).startsWith('provider-') ? String(rawProviderId) : `provider-${rawProviderId}`
    params.set('providerId', providerId)
  }

  const query = params.toString()
  return `/home/credentialing${query ? `?${query}` : ''}`
}

const getLeaveNotificationUrl = (notification = {}) => {
  const entityType = String(
    notification?.RelatedEntityType ?? notification?.relatedEntityType ?? notification?.EntityType ?? notification?.entityType ?? '',
  ).toLowerCase()

  if (entityType !== 'employeeleave' && entityType !== 'leave' && !entityType.includes('leave')) return ''

  const leaveId = notification?.LeaveID ?? notification?.LeaveId ?? notification?.leaveId ?? notification?.RelatedEntityId ?? notification?.relatedEntityId
  if (leaveId == null || String(leaveId).trim() === '') return '/home/hrm/leaves'

  return `/home/hrm/leaves?leaveId=${encodeURIComponent(String(leaveId))}`
}

const getNotificationRedirectUrl = (item = {}) => {
  const credentialingUrl = getCredentialingNotificationUrl(item)
  if (credentialingUrl) return credentialingUrl
  const text = `${item?.RelatedEntityType || item?.relatedEntityType || ''} ${item?.title || ''} ${item?.message || ''}`.toLowerCase()
  const taskId = item?.TaskID ?? item?.TaskId ?? item?.taskId ?? item?.RelatedEntityId ?? item?.relatedEntityId
  if (text.includes('task') && taskId != null && String(taskId).trim()) {
    return `/home/all-tasks?taskId=${encodeURIComponent(String(taskId))}`
  }
  const url = getLeaveNotificationUrl(item) || item?.Url || item?.url
  if (url) return url
  return (text.includes('task') || text.includes('subtask')) ? (item?.BoardId || item?.boardId ? `/home/kanban/${item.BoardId || item.boardId}` : '/home/all-tasks') : ''
}

const AppHeaderDropdown = () => {
  const history = useHistory()
  const [notificationOpen, setNotificationOpen] = useState(false)
  const [notificationLoading, setNotificationLoading] = useState(false)
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loggedUserId, setLoggedUserId] = useState('')
  const [theme, setTheme] = useState(readAppTheme)
  const [isThemeSwitching, setIsThemeSwitching] = useState(false)
  const [loggedInUserIdentity] = useState(() => readLoggedInUserIdentity())
  const [referenceCache, setReferenceCache] = useState(() => readNotificationReferenceCache())
  const [birthdayUsers, setBirthdayUsers] = useState(() => readBirthdayUsersCache())
  const notificationRef = useRef(null)
  const notificationRequestsRef = useRef(new Map())
  const wasNotificationOpenRef = useRef(false)
  const themeSwitchTimeoutRef = useRef(null)
  const themeSwitchFrameRef = useRef(null)

  const logout = () => {
    let role = getRolefromCookie()
    removeCookie()
    history.push('/login')
    // const cookies = new Cookies()
    // cookies.remove('jwt_token')
    // history.push('/login')
  }

  const isDarkTheme = theme !== 'light'

  const finishThemeSwitchAfterPaint = () => {
    window.clearTimeout(themeSwitchTimeoutRef.current)

    const hideLoader = () => {
      themeSwitchTimeoutRef.current = window.setTimeout(() => {
        setIsThemeSwitching(false)
      }, 250)
    }

    if (typeof window.requestAnimationFrame === 'function') {
      themeSwitchFrameRef.current = window.requestAnimationFrame(() => {
        themeSwitchFrameRef.current = window.requestAnimationFrame(hideLoader)
      })
      return
    }

    hideLoader()
  }

  const toggleTheme = (event) => {
    event.preventDefault()
    event.stopPropagation()

    if (isThemeSwitching) return

    const nextTheme = isDarkTheme ? 'light' : 'dark'
    setIsThemeSwitching(true)
    localStorage.setItem(APP_THEME_STORAGE_KEY, nextTheme)
    applyAppTheme(nextTheme)
    setTheme(nextTheme)
    window.dispatchEvent(new CustomEvent('provio-theme-change', { detail: { theme: nextTheme } }))
    finishThemeSwitchAfterPaint()
  }

  useEffect(() => {
    const syncTheme = (event) => {
      const nextTheme = event?.detail?.theme || readAppTheme()
      applyAppTheme(nextTheme)
      setTheme(nextTheme)
    }

    window.addEventListener('provio-theme-change', syncTheme)
    window.addEventListener('storage', syncTheme)

    return () => {
      window.removeEventListener('provio-theme-change', syncTheme)
      window.removeEventListener('storage', syncTheme)
      window.clearTimeout(themeSwitchTimeoutRef.current)
      if (themeSwitchFrameRef.current) {
        window.cancelAnimationFrame(themeSwitchFrameRef.current)
      }
    }
  }, [])

  const getStoredUserId = () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'))
      return userData?.Id || userData?.ID || userData?.id || userData?.UserId || userData?.userId || ''
    } catch {
      return ''
    }
  }

  const getStoredUsername = () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'))
      return userData?.Username || userData?.UserName || userData?.username || ''
    } catch {
      return ''
    }
  }

  const getLastSeenStorageKey = (userId) => `${NOTIFICATION_LAST_SEEN_KEY}_${userId || 'anonymous'}`

  const getLastSeenAt = useCallback((userId) => {
    if (!userId) return ''

    try {
      return localStorage.getItem(getLastSeenStorageKey(userId)) || ''
    } catch {
      return ''
    }
  }, [])

  const setLastSeenAt = useCallback((userId, value) => {
    if (!userId || !value) return

    try {
      localStorage.setItem(getLastSeenStorageKey(userId), value)
    } catch {
      // Ignore localStorage write failures and keep UI functional.
    }
  }, [])

  const getUnreadCount = useCallback(async (userId) => {
    if (!userId) {
      setUnreadCount(0)
      return 0
    }

    try {
      const username = getStoredUsername()
      const response = await SaveNotification({
        mode: 'UNREAD_COUNT',
        userId,
      })
      let totalCount = parseUnreadCountResponse(response)

      if (username && username !== userId) {
        try {
          const userResponse = await SaveNotification({
            mode: 'UNREAD_COUNT',
            userId: username,
          })
          totalCount += parseUnreadCountResponse(userResponse)
        } catch (err) {
          console.error('Failed to load unread count for username:', err)
        }
      }

      setUnreadCount(totalCount)
      return totalCount
    } catch (error) {
      console.error('Failed to load unread notification count:', error)
      return 0
    }
  }, [])

  const sortNotificationsByDate = (items = []) =>
    [...items].sort((a, b) => {
      const aTime = new Date(a?.createdDate || 0).getTime()
      const bTime = new Date(b?.createdDate || 0).getTime()

      return bTime - aTime
    })
  const parseUnreadCountResponse = (response) => {
    try {
      const parsed =
        typeof response?.d === 'string'
          ? JSON.parse(response.d)
          : response?.d ?? response

      const dataArray =
        Array.isArray(parsed?.data) ? parsed.data :
          Array.isArray(parsed?.Data) ? parsed.Data :
            []

      const candidates = [
        parsed?.count,
        parsed?.Count,
        parsed?.unreadCount,
        parsed?.UnreadCount,
        dataArray?.[0]?.count,
        dataArray?.[0]?.Count,
        dataArray?.[0]?.unreadCount,
        dataArray?.[0]?.UnreadCount,
        parsed?.data?.count,
        parsed?.data?.Count,
        parsed?.data?.unreadCount,
        parsed?.data?.UnreadCount,
        parsed?.Data?.count,
        parsed?.Data?.Count,
        parsed?.Data?.unreadCount,
        parsed?.Data?.UnreadCount,
        parsed,
      ]

      const firstValue = candidates.find((value) => value !== undefined && value !== null)
      const numericValue = Number(firstValue)

      return Number.isFinite(numericValue) ? numericValue : 0
    } catch (error) {
      console.error('Unread count parse error:', error)
      return 0
    }
  }
  const parseNotificationResponse = (response) => {
    try {
      const parsed =
        typeof response?.d === 'string'
          ? JSON.parse(response.d)
          : response?.d ?? response
      console.log('response', response)
      console.log('parsed', parsed)
      console.log('parsed.data', parsed?.data)
      if (Array.isArray(parsed?.data)) return parsed.data
      if (Array.isArray(parsed?.Data)) return parsed.Data
      if (Array.isArray(parsed?.notifications)) return parsed.notifications
      if (Array.isArray(parsed?.Notifications)) return parsed.Notifications
      if (Array.isArray(parsed)) return parsed


    } catch (error) {
      console.error('Notification response parse error:', error)
    }

    return []
  }
  const parseBooleanLikeValue = (value) => {
    if (value === true || value === false) return value
    if (value === 1 || value === 0) return Boolean(value)

    const normalizedValue = String(value ?? '').trim().toLowerCase()
    if (!normalizedValue) return null
    if (['true', '1', 'yes', 'y', 'read', 'seen', 'opened', 'viewed'].includes(normalizedValue)) return true
    if (['false', '0', 'no', 'n', 'unread', 'new', 'pending'].includes(normalizedValue)) return false

    return null
  }

  const resolveNotificationReadState = (item = {}, userId = '') => {
    const id = String(item?.NotificationId ?? item?.notificationId ?? item?.ID ?? item?.Id ?? item?.id ?? '')
    const uname = getStoredUsername()
    if (id && (readNotificationReadIds(userId).has(id) || (uname && readNotificationReadIds(uname).has(id)))) return true

    const directReadCandidates = [
      item?.IsRead,
      item?.isRead,
      item?.Read,
      item?.read,
      item?.HasRead,
      item?.hasRead,
    ]

    for (const candidate of directReadCandidates) {
      const parsedValue = parseBooleanLikeValue(candidate)
      if (parsedValue !== null) return parsedValue
    }

    const unreadCandidates = [
      item?.Unread,
      item?.unread,
      item?.IsUnread,
      item?.isUnread,
    ]

    for (const candidate of unreadCandidates) {
      const parsedValue = parseBooleanLikeValue(candidate)
      if (parsedValue !== null) return !parsedValue
    }

    const seenCandidates = [
      item?.Seen,
      item?.seen,
      item?.IsSeen,
      item?.isSeen,
    ]

    for (const candidate of seenCandidates) {
      const parsedValue = parseBooleanLikeValue(candidate)
      if (parsedValue !== null) return parsedValue
    }

    const statusCandidates = [
      item?.Status,
      item?.status,
      item?.NotificationStatus,
      item?.notificationStatus,
    ]

    for (const candidate of statusCandidates) {
      const parsedValue = parseBooleanLikeValue(candidate)
      if (parsedValue !== null) return parsedValue
    }

    return false
  }

  const normalizeNotificationRecord = (item = {}, userId = '') => {
    const isRead = resolveNotificationReadState(item, userId)

    return {
      ...item,
      id:
        item?.NotificationId ??
        item?.notificationId ??
        item?.ID ??
        item?.Id ??
        item?.id ??
        `${item?.CreatedDate ?? item?.createdDate ?? Date.now()}`,
      title:
        item?.Title ??
        item?.title ??
        item?.NotificationTitle ??
        item?.notificationTitle ??
        item?.Subject ??
        item?.subject ??
        'Notification',
      message:
        item?.Message ??
        item?.message ??
        item?.Description ??
        item?.description ??
        item?.Body ??
        item?.body ??
        item?.NotificationText ??
        item?.notificationText ??
        '',
      createdDate:
        item?.CreatedDate ??
        item?.createdDate ??
        item?.NotificationDate ??
        item?.notificationDate ??
        item?.Date ??
        item?.date ??
        '',
      isRead,
    }
  }

  const getNotifications = useCallback(async (userId) => {
    if (!userId) {
      setNotifications([])
      return []
    }

    setNotificationLoading(true)
    try {
      const username = getStoredUsername()
      const response = await SaveNotification('GET', userId)
      let apiNotifications = parseNotificationResponse(response).map((item) => normalizeNotificationRecord(item, userId))
      const reminderResults = await Promise.allSettled([
        SaveTasks({ action: 'GET_BY_ASSIGNEDTOME', createdBy: userId, assignedUsers: [{ UserId: userId, Username: username }] }),
        providerCredentialingCRUD('', userId, 'ASSIGNED_TO_ME'),
      ])
      const reminderNotifications = reminderResults.flatMap(({ status, value }, index) => {
        if (status !== 'fulfilled') return []
        const raw = typeof value?.d === 'string' ? JSON.parse(value.d) : value?.d ?? value
        return (raw?.data || raw?.Data || raw || []).flatMap((task) => {
          const subtaskReminders = buildSubtaskReminders(task, userId, username, index).map((item) => normalizeNotificationRecord(item, userId))
          const reminderDate = task?.ReminderDate || task?.reminderDate || task?.reminder_date || ''
          const date = parseUSDateValue(reminderDate)
          if (!date || date > new Date()) return subtaskReminders
          const title = task?.Title || task?.title || 'Task'
          const id = task?.TaskId || task?.id || task?.Id || title
          return [...subtaskReminders, normalizeNotificationRecord({ id: `reminder:${index}:${id}:${reminderDate}`, title: index ? 'Credentialing Reminder' : 'Task Reminder', message: `The due date for the "${title}" is ${formatValueToMMDDYYYY(reminderDate) || reminderDate}`, createdDate: reminderDate }, userId)]
        }).filter(Boolean)
      })

      if (username && username !== userId) {
        try {
          const userResponse = await SaveNotification('GET', username)
          const usernameNotifications = parseNotificationResponse(userResponse).map((item) => normalizeNotificationRecord(item, username))
          apiNotifications = [...apiNotifications, ...usernameNotifications]
        } catch (err) {
          console.error('Failed to load notifications for username:', err)
        }
      }

      // Deduplicate by ID
      const uniqueMap = new Map()
      apiNotifications.forEach((item) => {
        if (item && item.id) {
          uniqueMap.set(item.id, item)
        }
      })
      apiNotifications = Array.from(uniqueMap.values())

      const birthdayNotifications = buildBirthdayNotifications(
        birthdayUsers.length ? birthdayUsers : readBirthdayUsersCache(),
        loggedInUserIdentity,
        userId,
      )
      const allParsed = [...birthdayNotifications, ...reminderNotifications, ...apiNotifications]
      
      const loggedInIdentity = readLoggedInUserIdentity()
      const filtered = allParsed.filter(item => {
        if (!loggedInIdentity) return true
        const role = String(loggedInIdentity.role || '').toLowerCase()
        const isRestrictedRole = role.includes('provider')
        
        if (isRestrictedRole) {
          const msg = String(item.message || '').toLowerCase()
          if (msg.includes('submitted leave request')) {
            return false
          }
        }
        return true
      })

      const subtaskReminders = filtered.filter((item) => !item.isRead && String(item.id).startsWith('subtask-reminder:'))
      const rows = [...sortNotificationsByDate(subtaskReminders), ...sortNotificationsByDate(filtered.filter((item) => !subtaskReminders.includes(item))).slice(0, 10)]
      setNotifications(rows)
      return rows
    } catch (error) {
      console.error('Failed to load notifications:', error)
      const birthdayNotifications = buildBirthdayNotifications(
        birthdayUsers.length ? birthdayUsers : readBirthdayUsersCache(),
        loggedInUserIdentity,
        userId,
      )
      setNotifications(birthdayNotifications)
      return birthdayNotifications
    } finally {
      setNotificationLoading(false)
    }
  }, [birthdayUsers, loggedInUserIdentity])

  const refreshNotificationState = useCallback(async (userId, options = {}) => {
    if (!userId) {
      setNotifications([])
      setUnreadCount(0)
      return { notifications: [], unreadCount: 0 }
    }

    const { includeList = true, includeCount = true } = options
    const requestKey = JSON.stringify([userId, includeList, includeCount])
    const [listResult, countResult] = await (notificationRequestsRef.current.get(requestKey) || notificationRequestsRef.current.set(requestKey, Promise.all([
      includeList ? getNotifications(userId) : Promise.resolve([]),
      includeCount ? getUnreadCount(userId) : Promise.resolve(0),
    ])).get(requestKey)).finally(() => notificationRequestsRef.current.delete(requestKey))

    return {
      notifications: listResult,
      unreadCount: countResult,
    }
  }, [getNotifications, getUnreadCount])

  const formatNotificationDate = (value) =>
    formatValueToMMDDYYYY(value) || String(value || '')

  const handleNotificationToggle = async () => {
    const nextOpen = !notificationOpen
    setNotificationOpen(nextOpen)

    if (!nextOpen) return

    if (loggedUserId) {
      await refreshNotificationState(loggedUserId)
    }
  }

  const unreadItemsCount = notifications.filter((item) => !item.isRead).length
  const displayUnreadCount = notifications.length ? unreadItemsCount : unreadCount
  const accountDisplayName = loggedInUserIdentity?.name || loggedInUserIdentity?.username || 'User'
  const accountRoleLabel = formatRoleLabel(loggedInUserIdentity?.role || 'User')
  const accountInitials = getInitials(accountDisplayName, loggedInUserIdentity?.username)
  const notificationIdentityMap = useMemo(
    () => buildNotificationIdentityMap(referenceCache, loggedInUserIdentity),
    [referenceCache, loggedInUserIdentity],
  )

  useEffect(() => {
    let cancelled = false

    const loadBirthdayUsers = async () => {
      try {
        const response = await RegisterUser('', '', '', '', 'get_all')

        if (cancelled) return

        const users = parseBirthdayUserList(response).map(normalizeBirthdayUser)

        if (users.length) {
          setBirthdayUsers(users)
          writeBirthdayUsersCache(users)
        }
        setReferenceCache((previous) => {
          const nextCache = { ...previous, users: users.length ? users : previous.users }
          writeNotificationReferenceCache(nextCache)
          return nextCache
        })
      } catch (error) {
        console.error('Failed to load users for birthday notifications:', error)
      }
    }

    loadBirthdayUsers()

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if ((referenceCache.users?.length || 0) > 0 || (referenceCache.providers?.length || 0) > 0 || (referenceCache.employees?.length || 0) > 0) {
      return undefined
    }

    let cancelled = false

    const loadReferenceData = async () => {
      try {
        const [providerModule, employeeModule, hrmUtilsModule] = await Promise.all([
          import('../../services/general/ProviderOnboardingCRUD.api'),
          import('../../services/general/EmployeeDetailsCRUD.api'),
          import('../../views/dashboard/hrm/HRMProviderUtils'),
        ])

        const [userResponse, provResponse, empResponse] = await Promise.all([
          RegisterUser('', '', '', '', 'get_all').catch(() => null),
          providerModule.default({ mode: 'SELECTALL' }).catch(() => null),
          employeeModule.default({ mode: 'SELECTALL' }).catch(() => null),
        ])

        if (cancelled) return

        const {
          parseCrudListResponse,
          parseUserListResponse,
          normalizeEmployeeDetailsRecord,
          normalizeProviderOnboardingRecord,
          normalizeUserRecord,
        } = hrmUtilsModule

        const nextUsers = parseUserListResponse(userResponse).map((item) => normalizeUserRecord(item))
        const nextProviders = parseCrudListResponse(provResponse).map((item) => normalizeProviderOnboardingRecord(item))
        const nextEmployees = parseCrudListResponse(empResponse).map((item) => normalizeEmployeeDetailsRecord(item))
        const nextCache = { users: nextUsers, providers: nextProviders, employees: nextEmployees }

        setReferenceCache(nextCache)
        writeNotificationReferenceCache(nextCache)
      } catch (error) {
        console.error('Failed to load notification identity references:', error)
      }
    }

    loadReferenceData()

    return () => {
      cancelled = true
    }
  }, [referenceCache])

  useEffect(() => {
    if (!loggedUserId) return

    const hasReferenceData = (referenceCache.users?.length || 0) > 0 || (referenceCache.providers?.length || 0) > 0 || (referenceCache.employees?.length || 0) > 0
    if (!hasReferenceData) return

    refreshNotificationState(loggedUserId, { includeList: true, includeCount: false })
  }, [loggedUserId, referenceCache, refreshNotificationState])

  const handleNotificationClick = (item) => {
    setNotificationOpen(false)
    if (!item.isRead) {
      setNotifications((prev) => prev.map((n) => (n.id === item.id ? { ...n, isRead: true } : n)))
      setUnreadCount((prev) => Math.max(0, prev - 1))
      if (item.isSyntheticBirthday) markBirthdayNotificationsSeen(loggedUserId, [item.id])
      const uids = [loggedUserId, getStoredUsername()].filter((u, i, a) => u && a.indexOf(u) === i)
      uids.forEach((u) => {
        markNotificationReadLocally(u, item.id)
        SaveNotification({ mode: 'MARK_READ', userId: u, notificationId: item.id }).catch(() => {})
      })
    }
    const url = getNotificationRedirectUrl(item)
    if (url) /^https?:\/\//i.test(url) ? (window.location.href = url) : history.push(url)
  }

  useEffect(() => {
    if (!notificationOpen) return undefined

    const handleOutsideClick = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setNotificationOpen(false)
      }
    }

    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [notificationOpen])
  const refreshStateRef = useRef(refreshNotificationState)
  useEffect(() => {
    refreshStateRef.current = refreshNotificationState
  }, [refreshNotificationState])

  useEffect(() => {
    if (!loggedUserId) return undefined

    const refreshFromEvent = () => {
      refreshStateRef.current(loggedUserId)
    }

    const intervalId = window.setInterval(() => {
      refreshStateRef.current(loggedUserId, { includeList: true, includeCount: true })
    }, 15000)

    const handleFocus = () => {
      refreshStateRef.current(loggedUserId)
    }

    window.addEventListener('notifications:refresh', refreshFromEvent)
    window.addEventListener('focus', handleFocus)

    return () => {
      window.clearInterval(intervalId)
      window.removeEventListener('notifications:refresh', refreshFromEvent)
      window.removeEventListener('focus', handleFocus)
    }
  }, [loggedUserId])

  useEffect(() => {
    const initializeNotifications = async () => {
      const userId = getStoredUserId()
      setLoggedUserId(userId)
      if (userId) {
        await refreshNotificationState(userId)
      }
    }

    initializeNotifications()
  }, [refreshNotificationState])


  return (
    <div className="d-flex align-items-center gap-2">
      {isThemeSwitching && (
        <div className="theme-switch-loader-overlay" data-theme={theme} aria-live="polite" aria-label="Applying theme">
          <Pageloader isLoading />
        </div>
      )}
      {(
        <div ref={notificationRef} className="app-notification" data-theme={theme}>
          <button
            type="button"
            className="app-notification__button"
            onClick={handleNotificationToggle}
            aria-label="Notifications"
          >
            <Bell size={22} className="app-notification__icon" />
            {displayUnreadCount > 0 && (
              <span className={`app-notification__badge ${displayUnreadCount > 9 ? 'app-notification__badge--wide' : ''}`}>
                {displayUnreadCount > 99 ? '99+' : displayUnreadCount}
              </span>
            )}
          </button>

          {notificationOpen && (
            <div className="app-notification__panel">
              <div className="app-notification__header">
                <span className="app-notification__title">Notifications</span>
                <span className="app-notification__unread">{displayUnreadCount} unread</span>
              </div>

              {notificationLoading ? (
                <div className="app-notification__loading">
                  <CSpinner size="sm" className="app-notification__spinner" />
                </div>
              ) : notifications.length === 0 ? (
                <div className="app-notification__empty">
                  No Notifications Found.
                </div>
              ) : (
                notifications.map((item, index) => (
                  <div
                    key={item.id || index}
                    className={`app-notification__item ${item.isRead ? '' : 'app-notification__item--unread'} ${index === notifications.length - 1 ? 'app-notification__item--last' : ''}`}
                    style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}
                    onClick={() => handleNotificationClick(item)}
                  >
                    <div style={{ width: 8, minWidth: 8, paddingTop: 5 }}>
                      {!item.isRead && (
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--ui-color-primary, #1A76D3)', display: 'block' }} />
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="app-notification__item-title" style={{ marginBottom: item.message ? 4 : 0 }}>
                        {formatNotificationLabel(item.title)}
                      </div>
                      {item.message && (
                        <div className="app-notification__message">
                          {formatNotificationMessage(item, notificationIdentityMap, loggedInUserIdentity)}
                        </div>
                      )}
                      {item.createdDate && (
                        <div className="app-notification__date">
                          {formatNotificationDate(item.createdDate)}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}
      <CDropdown variant="nav-item">
        <CDropdownToggle placement="bottom-end" className="py-0" caret={false}>
          <CAvatar src={iconImg} size="md" />
        </CDropdownToggle>
        <CDropdownMenu className="pt-0 app-account-menu" placement="bottom-end">
          <div className="app-account-menu__profile">
            <div className="app-account-menu__avatar" aria-hidden="true">
              {accountInitials}
            </div>
            <div className="app-account-menu__name">{accountDisplayName}</div>
            <div className="app-account-menu__role">{accountRoleLabel}</div>
          </div>
          <CDropdownDivider />
          {/* <CDropdownItem href="#" className="theme-toggle-menu-item app-account-menu__theme-item" onClick={toggleTheme} disabled={isThemeSwitching}>
            <div className="app-account-menu__theme-copy">
              {isDarkTheme ? <Moon size={18} className="me-2" /> : <Sun size={18} className="me-2" />}
              <span>Dark theme</span>
            </div>
            <span className={`app-theme-switch ${isDarkTheme ? 'app-theme-switch--active' : ''}`} aria-hidden="true">
              <span className="app-theme-switch__thumb" />
            </span>
          </CDropdownItem> */}
          <CDropdownDivider />
          <CDropdownItem onClick={logout}>
            <CIcon icon={cilAccountLogout} className="me-2" />
            Log out
          </CDropdownItem>
        </CDropdownMenu>
      </CDropdown>
    </div>
  )
}

export default AppHeaderDropdown
